import {
  sanitizeJobLog,
  TERMINAL_JOB_STATUSES,
} from '../Gondola/gondolaJobStatus';

const MARKER_PATTERN = /^(?:\[PRODUCT_SYNC_PROGRESS\]\s*|(?:\d+(?:\.\d+)?) - (?:DEBUG|INFO|WARNING|ERROR|CRITICAL) - (?:(?:\[(?:runId|instanceKey|companyId)=[^\]\r\n]*\])\s*)*\[PRODUCT_SYNC_PROGRESS\]\s*)(\{.*\})\s*$/;
const STAGES = new Set(['run', 'aggregation', 'chroma_sync']);
const STATES = new Set(['started', 'progress', 'completed', 'skipped', 'failed']);
const COUNT_FIELDS = ['sourceFiles', 'products', 'services', 'upserted', 'deleted'];
const MAX_COUNT = 10 * 1000 * 1000;

const safeText = (value, maxLength = 512) => (
  typeof value === 'string' ? value.slice(0, maxLength) : null
);

const safeCount = value => (
  Number.isSafeInteger(value) && value >= 0 && value <= MAX_COUNT ? value : null
);

const sanitizeProgress = progress => {
  if (!progress || typeof progress !== 'object' || Array.isArray(progress)) return null;
  const completed = safeCount(progress.completed);
  const total = safeCount(progress.total);
  const unit = safeText(progress.unit, 32);
  if (completed == null && total == null && !unit) return null;
  return {
    ...(completed != null ? { completed } : {}),
    ...(total != null ? { total } : {}),
    ...(unit ? { unit } : {}),
  };
};

const sanitizeCounts = counts => {
  if (!counts || typeof counts !== 'object' || Array.isArray(counts)) return null;
  const result = COUNT_FIELDS.reduce((values, field) => {
    const value = safeCount(counts[field]);
    return value == null ? values : { ...values, [field]: value };
  }, {});
  return Object.keys(result).length ? result : null;
};

const sanitizeError = error => {
  if (!error || typeof error !== 'object' || Array.isArray(error)) return null;
  const code = safeText(error.code, 128);
  const message = safeText(error.message, 1000);
  if (!code && !message) return null;
  return {
    ...(code ? { code } : {}),
    ...(message ? { message } : {}),
  };
};

export const sanitizeProductSyncLog = sanitizeJobLog;

export const parseProductSyncMarkers = (log, expectedRunId) => {
  const markers = sanitizeProductSyncLog(log).reduce((result, line) => {
    const match = line.match(MARKER_PATTERN);
    if (!match) return result;
    try {
      const marker = JSON.parse(match[1]);
      if (!marker || marker.version !== 1) return result;
      if (!Number.isSafeInteger(marker.seq) || marker.seq < 1 || marker.seq > MAX_COUNT) return result;
      if (!STAGES.has(marker.stage) || !STATES.has(marker.state)) return result;
      if (typeof marker.runId !== 'string' || (expectedRunId && marker.runId !== expectedRunId)) {
        return result;
      }
      const timestamp = safeText(marker.timestamp, 64);
      if (!timestamp || Number.isNaN(Date.parse(timestamp))) return result;
      return [...result, {
        version: 1,
        runId: marker.runId,
        seq: marker.seq,
        timestamp,
        stage: marker.stage,
        state: marker.state,
        progress: sanitizeProgress(marker.progress),
        counts: sanitizeCounts(marker.counts),
        dataHash: safeText(marker.dataHash, 128),
        error: sanitizeError(marker.error),
      }];
    } catch (_) {
      return result;
    }
  }, []);

  const bySequence = markers.reduce((result, marker) => ({
    ...result,
    [marker.seq]: marker,
  }), {});
  return Object.keys(bySequence)
    .map(Number)
    .sort((a, b) => a - b)
    .map(seq => bySequence[seq]);
};

export const productSyncConnectionKey = ({ app, integrationId, hint }) => (
  `${app || integrationId || ''}::${hint || ''}`
);

export const productSyncRunStatus = run => (
  run && (run.outcome || run.status)
);

export const productSyncCurrentRunDisplayStatus = ({
  currentRun,
  isPolling,
  jobStatus,
  markers = [],
}) => {
  const durableStatus = productSyncRunStatus(currentRun);
  if (!isPolling) return durableStatus;

  const normalizedJobStatus = typeof jobStatus === 'string' ? jobStatus.toLowerCase() : '';
  if (TERMINAL_JOB_STATUSES.has(normalizedJobStatus)) return jobStatus;

  const terminalRunMarker = markers.find(marker => (
    marker.stage === 'run' && ['completed', 'skipped', 'failed'].includes(marker.state)
  ));
  if (terminalRunMarker && terminalRunMarker.state === 'failed') return 'failed';
  if (terminalRunMarker) return 'succeeded';
  if (markers.length) return 'running';
  return jobStatus || durableStatus;
};

const COUNT_FIELDS_BY_STAGE = {
  aggregation: [
    ['sourceFiles', 'Source files read'],
    ['products', 'Products'],
    ['services', 'Services'],
  ],
  chroma_sync: [
    ['products', 'Products'],
    ['services', 'Services'],
    ['upserted', 'Upserted'],
    ['deleted', 'Deleted'],
  ],
};

export const productSyncStageCounts = (stage, counts) => (
  (COUNT_FIELDS_BY_STAGE[stage] || [])
    .filter(([field]) => counts && counts[field] !== null && counts[field] !== undefined)
    .map(([field, label]) => ({ label, value: counts[field] }))
);

export const formatProductSyncAction = action => ({
  aggregate: 'Aggregate only',
  aggregate_and_populate: 'Aggregate and populate Chroma',
  populate_snapshot: 'Populate selected snapshot',
}[action] || (action ? String(action).replace(/_/g, ' ') : '—'));

export const formatProductSyncStage = stage => ({
  run: 'Run',
  aggregation: 'Aggregation',
  chroma_sync: 'Chroma population',
}[stage] || String(stage || '').replace(/_/g, ' '));

export const formatProductSyncStageState = state => ({
  started: 'Started',
  progress: 'In progress',
  completed: 'Completed',
  skipped: 'Skipped',
  failed: 'Failed',
}[state] || String(state || '').replace(/_/g, ' '));

export const formatProductSyncStatus = status => ({
  accepted: 'Queued',
  pending: 'Queued',
  queued: 'Queued',
  active: 'Running',
  started: 'Running',
  working: 'Running',
  running: 'Running',
  succeeded: 'Succeeded',
  success: 'Succeeded',
  completed: 'Succeeded',
  processed: 'Succeeded',
  failed: 'Failed',
  failure: 'Failed',
  error: 'Failed',
  stuck: 'Failed',
  canceled: 'Cancelled',
  cancelled: 'Cancelled',
  revoked: 'Cancelled',
  no_source: 'No source data',
  no_services: 'No services',
  skipped_no_change: 'No changes',
  paused: 'Paused',
  enabled: 'Enabled',
}[status] || (status ? String(status).replace(/_/g, ' ') : 'No runs yet'));
